/**
 * THE ONE FIELD TREATMENT (P11), AS A VALUE BOTH SIDES READ.
 *
 * ===================== WHY THIS IS A MODULE AND NOT A REPEATED STRING ======
 * P11 says a control has to look like it can receive something, and it was
 * written against a defect Amir named by eye: Manage's search box was
 * `text-xs px-2 py-1` on a flat cloudburst, which is smaller type than the value
 * it holds on the same background as the cards beside it. The shell's period
 * select was the same shape.
 *
 * FE-9.8b fixed the shell's select and deferred Manage's two inputs to 9.8c,
 * with a PARITY CHECK ruled to run at this chunk's review: the two treatments
 * must be byte-consistent. Two hand-written copies cannot be byte-consistent for
 * longer than one edit, and this OS has already paid that bill three times over
 * (five transcriptions of the lifted card, five of the charlotte pill, three of
 * the `Fact` box). So parity is STRUCTURAL here rather than asserted between two
 * literals: there is one string, both call sites import it, and a divergence has
 * to be written deliberately rather than drifted into.
 *
 * ===================== WHAT IS IN THE BASE, AND WHAT IS NOT ================
 * In: the recessed well one step under the surface the field sits on, the inset
 * top edge, the hairline that warms to charlotte on hover, the charlotte border
 * on focus beside the ring, `px-3 py-2`, and `text-sm`, which is the floor P11
 * actually cares about (never smaller than the value it will hold).
 *
 * Out, deliberately, three things:
 *
 * `lowercase` is the SHELL's, not the base's. The period select renders copy
 * from a module and the house register is lowercase; a free-text search box
 * renders what a teacher typed, and CSS-lowercasing a person's name back at them
 * while they type it is a worse defect than the one this treatment fixes. Same
 * argument the Button primitive makes for not putting `lowercase` on its base.
 *
 * `disabled:opacity-38` is the CALL SITE's, because only some of these fields
 * can disable and Manage's dead-control gate (`ManagePolish.test.jsx`) requires
 * every control carrying `disabled=` to carry a `disabled:` class beside it. A
 * base that shipped the treatment unconditionally would satisfy that gate for
 * fields that can never disable, which is how a gate stops meaning anything.
 *
 * THE INSET IS A CLASS AND NEVER AN INLINE `boxShadow`, and that is a gate
 * constraint rather than a preference: `focusRing.utility.test.js` requires a
 * focusable element's decorative shadow to stay overridable by the focus ring,
 * and an inline shadow wins over the ring's own.
 */
export const FIELD_CLASS =
  "focus-visible:focus-ring rounded-md border border-white/10 bg-background-blackpearl px-3 py-2 text-sm text-lv1 shadow-[inset_0_1px_0_rgba(0,0,0,0.35)] transition-colors duration-fast hover:border-charlotte/30 focus-visible:border-charlotte/60";

/**
 * The label that sits above a field, in the OS overline treatment.
 *
 * P11 names the label as part of the treatment, and it is the same overline
 * `tierSeparationCues.test.jsx` already pins everywhere else in the OS.
 */
export const FIELD_LABEL_CLASS =
  "text-xs font-semibold uppercase tracking-button text-lv4";
